import tokens from '@kluein/design-tokens';
import { Box, Text, Anchor, Tag, Button } from '@kluein/klue-ui';
import { CaretRight, StackSimple } from '@phosphor-icons/react';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useRedirect } from 'hooks/use-redirect';
import {
  AnalyticsAction,
  AnalyticsCategory,
  useAnalytics,
} from 'lib/analytics';
import {
  Suggestion,
  type SuggestionType,
} from 'store/models/search-suggestions/searchSuggestions.model';
import TRACKING_IDS from 'tracking-ids';

export const useSearchSuggestionHandlers = (
  onSuggestionsClose: () => void,
  setInputValue: (value: string) => void,
  resetSuggestions: () => void,
  search: (options: {
    terms?: string;
    tagId?: number;
    tagOp?: string;
    reset?: boolean;
  }) => void,
) => {
  const { t } = useTranslation(['Common', 'Search']);
  const navigate = useNavigate();
  const { redirectToCompany } = useRedirect();
  const { logEvent } = useAnalytics();

  const onSuggestionSelect = useCallback(
    (suggestion: SuggestionType) => {
      resetSuggestions();
      onSuggestionsClose();

      if (suggestion.type === Suggestion.TAG) {
        setInputValue('');
        logEvent({
          event: {
            category: AnalyticsCategory.search,
            action: AnalyticsAction.tagSearch,
            label: suggestion.value.name,
          },
        });
        search({
          tagId: suggestion.value.id,
          tagOp: 'add',
          reset: true,
        });
        return;
      }

      if (suggestion.type === Suggestion.BATTLECARD) {
        setInputValue('');
        logEvent({
          event: {
            category: AnalyticsCategory.search,
            action: AnalyticsAction.battlecardSearch,
            label: suggestion.value.url,
          },
        });
        navigate(suggestion.value.url);
        return;
      }

      setInputValue(suggestion.value);
      search({ terms: suggestion.value, reset: true });
    },
    [
      logEvent,
      onSuggestionsClose,
      navigate,
      resetSuggestions,
      search,
      setInputValue,
    ],
  );

  const suggestionComponent = useCallback(
    (item: SuggestionType): ReactNode => {
      switch (item.type) {
        case Suggestion.TAG:
          return (
            <Box direction="row" align="center" gap="xsmall">
              {!!item.value.synonym && (
                <Box direction="row" align="center" gap="xsmall">
                  <Anchor
                    a11yTitle={t('Common:navBar.search.searchFor', {
                      item: item.value.name,
                    })}
                    label={item.value.synonym}
                  />
                  <CaretRight size={tokens.iconSize.xsmall} />
                </Box>
              )}
              <Button
                variant="plain"
                onMouseDown={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onSuggestionSelect(item);
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.stopPropagation();
                    event.preventDefault();
                    onSuggestionSelect(item);
                  }
                }}
                a11yTitle={`Select tag ${item.value.name}`}
                focusIndicator={false}
              >
                <Tag variant="primary" label={item.value.name} />
              </Button>
            </Box>
          );
        case Suggestion.BATTLECARD:
          return (
            <Box
              direction="row"
              gap="xsmall"
              align="center"
              fill="horizontal"
              style={{ display: 'inline-flex' }}
            >
              <Anchor
                onMouseDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  redirectToCompany(item.value.rivalProfileId);
                  resetSuggestions();
                  onSuggestionsClose();
                  setInputValue('');
                }}
                title={`Go to the "${item.value.rivalName}" Board`}
                style={{ overflow: 'hidden' }}
                data-tracking-id={
                  TRACKING_IDS.global.appNavBar.globalSearch.suggestions
                    .battlecard.profile
                }
              >
                <Text truncate fontWeight="semibold">
                  {`${item.value.rivalName}`}
                </Text>
              </Anchor>
              <CaretRight
                color={tokens.color.neutral[700]}
                size={tokens.iconSize.xsmall}
              />
              <StackSimple
                color={tokens.color.neutral[1000]}
                size={tokens.iconSize.xsmall}
              />
              <Anchor
                onMouseDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onSuggestionSelect(item);
                }}
                title={`Go to the "${item.value.title}" Battlecard`}
                style={{ overflow: 'hidden' }}
                data-tracking-id={
                  TRACKING_IDS.global.appNavBar.globalSearch.suggestions
                    .battlecard.battlecardTitle
                }
              >
                <Text truncate fontWeight="semibold">
                  {item.value.title || t('Common:untitledBattlecard')}
                </Text>
              </Anchor>
            </Box>
          );
        case Suggestion.COMPLETION:
          return (
            <Anchor
              onMouseDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onSuggestionSelect(item);
              }}
              a11yTitle={t('Common:navBar.search.searchFor', { item })}
              label={item.value}
            />
          );
      }
    },
    [
      onSuggestionSelect,
      t,
      redirectToCompany,
      resetSuggestions,
      setInputValue,
      onSuggestionsClose,
    ],
  );

  return { onSuggestionSelect, suggestionComponent };
};

export const createSuggestionItems = (
  suggestions: SuggestionType[],
  suggestionComponent: (item: SuggestionType) => ReactNode,
) => {
  const BattleCardAndTagSuggestions = suggestions.filter(({ type }) =>
    [Suggestion.TAG, Suggestion.BATTLECARD].includes(type),
  );

  const filteredSuggestions =
    BattleCardAndTagSuggestions.length > 0
      ? [
          ...suggestions
            .filter(({ type }) => type === Suggestion.COMPLETION)
            .slice(0, 3),
          ...BattleCardAndTagSuggestions,
        ]
      : suggestions;

  return filteredSuggestions.map((item, i) => ({
    label: (
      <Box
        border={
          i !== 0 && {
            color: tokens.color.neutral.lightgrey.main,
            position: 'between',
            side: 'top',
          }
        }
        direction="row"
        align="center"
        pad="medium"
        key={i}
        flex={{ shrink: 0 }}
      >
        {suggestionComponent(item)}
      </Box>
    ),
    value: item,
  }));
};
